import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "../lib/smooth";
import { useCart } from "../lib/cart";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "Your corner", c: "" },
  { t: "shop, poured", c: "italic text-[#D62828]" },
  { t: "after dark.", c: "" },
];

const Line = ({ text, cls, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${cls}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: EASE }}>
      {text}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} style={{ perspective: 1200 }} className="relative mx-auto w-full max-w-[440px]">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
        className="arch relative aspect-[3/4] overflow-hidden border border-[#D62828]/30 bg-[#FFF6D6] shadow-[0_40px_120px_-30px_rgba(214,40,40,0.25)]">
        <motion.img src="/img/hero.jpg" alt="Bottles lit in the dark" initial={{ scale: 1.25 }} animate={{ scale: 1.05 }}
          transition={{ duration: 2.2, delay: 0.6, ease: EASE }} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_30%,transparent,rgba(13,13,15,0.75))]" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
          <span className="font-mono-g text-[11px] uppercase tracking-[0.3em] text-white">Fine wine · Craft · Spirits</span>
          <span className="font-display text-4xl italic text-[#FFC93C]">G.</span>
        </div>
      </motion.div>
      <motion.div style={{ rotate: -12 }} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.5, duration: 0.8, ease: EASE }}
        className="absolute -left-4 top-10 grid h-24 w-24 place-items-center rounded-full bg-[#FFC93C] text-center shadow-xl md:-left-10">
        <span className="font-mono-g text-[10px] uppercase leading-tight tracking-[0.15em] text-[#1C1917]">Click<br />&amp;<br />Collect</span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { setOpen } = useCart();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  return (
    <section ref={ref} className="relative overflow-hidden pt-[120px] pb-20 md:pt-[150px] md:pb-28">
      <div className="pointer-events-none absolute -right-40 top-0 h-[700px] w-[700px] rounded-full bg-[#D62828]/10 blur-[140px]" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[500px] w-[500px] rounded-full bg-[#FFC93C]/40 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 md:px-10 lg:grid-cols-[1.25fr_1fr]">
        <motion.div style={{ y: yText }}>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono-g mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#D62828]">
            <span className="h-px w-10 bg-[#D62828]" /> Independent off-licence · UK
          </motion.p>
          <h1 data-testid="guna-hero-title" className="font-display text-[15vw] leading-[0.92] tracking-[-0.02em] text-[#1C1917] sm:text-7xl lg:text-[8.5rem]">
            {LINES.map((l, i) => <Line key={l.t} text={l.t} cls={l.c} i={i} />)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-8 max-w-md text-base leading-relaxed text-stone-700 md:text-lg">
            Cold beer, good wine, proper spirits and every late-night essential. Build your basket online, pay in store when you collect.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4">
            <button data-testid="guna-hero-cta-browse" onClick={() => scrollToId("catalogue")}
              className="group flex items-center gap-3 rounded-full bg-[#D62828] px-7 py-4 text-sm font-semibold text-[#FFFFFF] transition-transform hover:-translate-y-0.5">
              Browse the shelves
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="guna-hero-cta-collect" onClick={() => setOpen(true)}
              className="rounded-full border border-black/20 px-7 py-4 text-sm text-[#1C1917] transition-colors hover:border-[#D62828] hover:text-[#D62828]">
              My collect basket
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y }}><TiltFrame /></motion.div>
      </div>
    </section>
  );
};
